import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Backdrop, { BackdropProps } from "@material-ui/core/Backdrop";
import Alert, { AlertProps } from "@material-ui/lab/Alert";
import clsx from "clsx";
import { defineMessages, useIntl } from "react-intl";

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    height: "100%",
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    color: theme.palette.common.white,
    zIndex: theme.zIndex.appBar - 1,
  },
}));

const msg = defineMessages({
  closeButtonTooltipLabel: {
    id: "fullscreenNotification.closeButtonTooltip.label",
    description: "Label of the close button tooltip.",
    defaultMessage: "Close",
  },
});

export type FullscreenNotificationProps = Omit<BackdropProps, "children"> & {
  /**
   * Alias for the `message` property.
   */
  children?: React.ReactNode;
  /**
   * Notification message displayed by the component.
   */
  message?: React.ReactNode;
  /**
   * Type of the notification, to adapt color and icon according to the message.
   */
  type?: "success" | "error" | "warning" | "info";
  /**
   * Callback applied to the close button onClick event.
   * If undefined, the close button will be hidden.
   */
  onCloseButtonClick?: AlertProps["onClose"];
};

/**
 * Component to display a notification message.
 *
 * You can add if you want a notification type to highlight the level of
 * severity of the message.
 *
 * @param props - The FullscreenNotification component props.
 */
function FullscreenNotification(props: FullscreenNotificationProps) {
  const {
    className,
    children,
    message,
    type = "info",
    onCloseButtonClick,
    ...other
  } = props;
  const classes = useStyles();
  const { formatMessage: t } = useIntl();

  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  return (
    <Backdrop className={rootClasses} {...other}>
      {other.open && (
        <Alert
          severity={type}
          onClose={onCloseButtonClick}
          closeText={t(msg.closeButtonTooltipLabel)}
        >
          {message || children}
        </Alert>
      )}
    </Backdrop>
  );
}

export default FullscreenNotification;
